import { useEffect, useState, type ReactNode } from "react";
import { Button, Dialog, DialogContent } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import {
  BookCafeFeature,
  BookCafeFeatureSlug,
  BOOK_CAFE_BOARD_GAMES,
  getBookCafeFeature,
} from "./book-cafe-content";

interface BookCafeStoryCard {
  eyebrow: string;
  title: string;
  description: string;
  imageSrc: string;
}

interface PropsType {
  userState: userState;
}

interface BookCafeStoryContent {
  introLabel: string;
  introContent?: ReactNode;
  quote: string;
  quoteEmphasis: string;
  sectionTitle: string;
  cards: BookCafeStoryCard[];
}

type BoardGameAudience = "all" | "toddler" | "child" | "teen" | "adult";

const BOARD_GAME_AUDIENCE_FILTERS: Array<{ id: BoardGameAudience; label: string }> = [
  { id: "all", label: "전체" },
  { id: "toddler", label: "유아" },
  { id: "child", label: "어린이" },
  { id: "teen", label: "청소년" },
  { id: "adult", label: "성인" },
];

const mainApi = new HttpMainApi();
const BOARD_GAME_LIST_COACH_MARK_SEEN_KEY = "hpoint.book-cafe.board-game-list-coach-mark.seen";

const getBoardGameAudience = (age: string): Exclude<BoardGameAudience, "all"> => {
  const minimumAge = Number.parseInt(age, 10);

  if (minimumAge <= 5) return "toddler";
  if (minimumAge <= 8) return "child";
  if (minimumAge <= 17) return "teen";
  return "adult";
};

const BOOK_CAFE_STORY_CONTENT: Partial<Record<BookCafeFeatureSlug, BookCafeStoryContent>> = {
  "game-zone": {
    introLabel: "엔터테인먼트 공간 소개",
    introContent: (
      <>
        <p>
          <strong>
            오락실 게임부터 다트, 민속놀이까지
            <br />
            세대를 넘어 함께 즐기는 우리만의 플레이 타임
          </strong>
        </p>
        <p>
          익숙한 아케이드 게임의 재미부터 집중력을 겨루는 다트, 온 가족이 함께하는 민속놀이까지 다양하게 즐길 수 있는 엔터테인먼트 공간입니다.
        </p>
        <p>
          아이들에게는 새로운 놀이의 즐거움을, 어른들에게는 익숙한 추억과 유쾌한 경쟁의 재미를 선사합니다. 가족과 친구가 한자리에 모여 가볍게 게임을 시작하고, 서로 점수를 겨루며 자연스럽게 웃음과 대화를 나눠보세요.
        </p>
        <p>승패보다 함께하는 순간이 더 즐거운 곳.</p>
        <p>
          <strong>가족과 이웃이 세대를 넘어 하나의 즐거움을 나누는 #엔터테인먼트입니다.</strong>
        </p>
      </>
    ),
    quote: "오락실 게임부터 다트까지",
    quoteEmphasis: "함께할수록 웃음이 커지는 우리만의 플레이 매치",
    sectionTitle: "엔터테인먼트 소개",
    cards: [
      {
        eyebrow: "ARCADE PLAY",
        title: "아케이드 게임",
        description: "익숙한 오락실 게임을 즐기며, 남녀노소 누구나 가볍게 시작하는 플레이 타임",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-arcade-kids-match-3d-v2.png",
      },
      {
        eyebrow: "DART CHALLENGE",
        title: "다트 챌린지",
        description: "한 발 한 발 집중해서 던지고, 가족과 친구들과 점수를 겨루는 짜릿한 한판",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-darts-teen-challenge-3d-v6.png",
      },
      {
        eyebrow: "TRADITIONAL PLAY",
        title: "온 가족 민속놀이",
        description: "명절마다 함께하던 윷놀이처럼, 세대가 둘러앉아 웃고 즐기는 온 가족 놀이 한판",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-yutnori-family-match-3d-v3.png",
      },
    ],
  },
  "birthday-dining": {
    introLabel: "프라이빗 파티 소개",
    introContent: (
      <>
        <p>
          <strong>
            생일과 기념일, 소소한 파티까지
            <br />{" "}
            우리끼리 즐기는 프라이빗 파티
          </strong>
        </p>
        <p>가족과 친구들이 편안하게 모여 특별한 순간을 나눌 수 있는 프라이빗 공간입니다.</p>
        <p>
          아이의 생일을 위한 작은 파티부터 가까운 가족과의 기념일, 아이와 함께하는 편안한 모임까지. 복잡한 준비와 부담은 덜고, 우리만의 공간에서 여유롭게 이야기를 나누며 소중한 시간을 보내보세요.
        </p>
        <p>
          아이들은 안전하고 편안하게 머물고, 어른들은 여유롭게 대화를 나누며 간식과 가벼운 식사를 함께할 수 있습니다. 특별한 날은 물론 평범한 하루의 작은 모임도 조금 더 특별한 추억이 됩니다.
        </p>
        <p>
          <strong>
            가까운 곳에서 편안하게,
            <br />{" "}
            소중한 사람들과 오롯이 함께하는 시간.
            <br />{" "}
            #프라이빗 파티에서 만나보세요.
          </strong>
        </p>
      </>
    ),
    quote: "생일과 기념일, 소소한 파티까지",
    quoteEmphasis: "우리끼리 즐기는 프라이빗 파티",
    sectionTitle: "파티 공간 소개",
    cards: [
      {
        eyebrow: "PRIVATE PARTY",
        title: "파티룸 대관",
        description: "부담 없이 준비하고, 우리끼리 프라이빗하게 즐기는 생일 파티",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-private-party-room-3d-v1.png",
      },
      {
        eyebrow: "FAMILY GATHERING",
        title: "아이와 함께하는 모임",
        description: "아이들은 안전하고 편안하게, 부모는 여유롭게 대화를 나누며 간식과 가벼운 식사를 함께하는 시간",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-family-moms-kids-gathering-3d-v1.png",
      },
      {
        eyebrow: "HOME PARTY",
        title: "친구들과 홈파티",
        description: "간식도 나누고 이야기도 나누며, 친구들끼리 편안하게 즐기는 우리만의 파티",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-teen-girls-home-party-3d-v2.png",
      },
    ],
  },
  "family-lounge": {
    introLabel: "패밀리 라운지 소개",
    introContent: (
      <>
        <p>
          <strong>아이들은 즐겁게, 어른들은 여유롭게</strong>
          <br />
          <strong>함께 머물며 각자의 시간을 즐기는 패밀리 라운지</strong>
        </p>
        <p>가족이나 지인과의 만남이 조금 더 이어졌으면 하는 날, 부담 없이 커피 한잔과 대화를 즐겨보세요.</p>
        <p>
          아이들은 가까운 곳에서 영화와 보드게임을 즐기고, 어른들은 편안하게 이야기를 나눌 수 있습니다.
          손님을 맞이한 날부터 식사 후 이어지는 자리, 아이와 함께하는 가벼운 모임까지 모두가 한 공간에서
          각자의 방식으로 편안한 시간을 보낼 수 있습니다.
        </p>
        <p><strong>아이도 어른도 편안해서, 함께하는 시간이 자연스럽게 길어지는 공간입니다.</strong></p>
      </>
    ),
    quote: "아이들은 즐겁게, 어른들은 여유롭게",
    quoteEmphasis: "함께 머물며 각자의 시간을 즐기는 패밀리 라운지",
    sectionTitle: "공간 소개",
    cards: [
      {
        eyebrow: "FAMILY GATHERING",
        title: "가족 모임",
        description: "온 가족이 한자리에 모여 식사와 놀이, 대화를 여유롭게 즐길 수 있는 프라이빗 커뮤니티 공간입니다.",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-family-gathering-3d-v1.png",
      },
      {
        eyebrow: "DINNER & PLAY",
        title: "뒤풀이 모임",
        description: "지인과 식사 후 마음껏 더 놀고 싶은 아이들을 위한 공간입니다.",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-after-dinner-play-3d-v1.png",
      },
      {
        eyebrow: "CASUAL GET-TOGETHER",
        title: "가벼운 모임",
        description: "아이들은 영화와 보드게임을 즐기고, 어른들은 편안히 대화에 집중할 수 있는 균형 잡힌 모임 공간입니다.",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-light-gathering-3d-v2.png",
      },
    ],
  },
  "resident-events": {
    introLabel: "주민 프로그램 소개",
    introContent: (
      <>
        <p>
          <strong>같은 즐거움으로 이웃과 가까워지는 시간</strong>
        </p>
        <p>
          책을 읽고 머무는 공간을 넘어, 함께 보고 응원하고 이야기를 나누는 순간까지.
          <br />{" "}
          북카페가 일상 가까이에서 이웃을 만나고 새로운 추억을 만들어가는 작은 커뮤니티 공간이 됩니다.
        </p>
        <p>
          아이들이 친구들과 함께 영화를 감상하는 특별한 오후, 모두가 한마음으로 경기를 응원하는 뜨거운 순간, 가벼운 먹거리와 함께 자연스럽게 이어지는 이웃과의 만남까지.
        </p>
        <p>거창한 약속이나 준비 없이도 같은 관심과 즐거움을 나누며 조금씩 가까워지는 시간.</p>
        <p>
          <strong>입주민 프로그램을 통해 우리 단지 안에서 함께하는 즐거움을 만나보세요.</strong>
        </p>
      </>
    ),
    quote: "같은 즐거움으로 이웃과 가까워지는 시간",
    quoteEmphasis: "함께 보고, 응원하고, 가볍게 만나는 주민 프로그램",
    sectionTitle: "프로그램 소개",
    cards: [
      {
        eyebrow: "KIDS CINEMA",
        title: "작지 않은 영화관",
        description: "어린이를 위한 이벤트 프로그램 운영 (입주민 아이들 대상으로 함께 영화 관람)",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-resident-kids-cinema-group-3d-v1.png",
      },
      {
        eyebrow: "CHEER TOGETHER",
        title: "함께 응원",
        description: "대형 이벤트 입주민이 함께 응원 (프로야구, 월드컵 등)",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-resident-baseball-rival-fans-3d-v1.png",
      },
      {
        eyebrow: "POP-UP GATHERING",
        title: "번개 모임",
        description: "치킨과 맥주, 과자와 음료를 함께 즐기는 치맥 데이",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-resident-pop-up-gathering-3d-v1.png",
      },
    ],
  },
  cinema: {
    introLabel: "시네마 공간 소개",
    introContent: (
      <>
        <p>
          빔프로젝터와 대형 스크린을 갖춘 <strong>#시네마</strong>는 가족과 이웃이 함께 다양한 콘텐츠를 즐길 수 있는 커뮤니티 공간입니다.
        </p>
        <p>
          보고 싶었던 영화와 애니메이션을 한층 큰 화면으로 감상하고, 모두가 기다려온 스포츠 경기를 함께 응원하는 등 일상 속 즐거운 순간을 가까운 곳에서 나눌 수 있습니다.
        </p>
        <p>아이들에게는 특별한 추억을, 어른들에게는 편안한 여가를 선사하는 곳.</p>
        <p>
          <strong>같은 화면을 바라보며 함께하는 시간이 더욱 특별해지는 #시네마를 만나보세요.</strong>
        </p>
      </>
    ),
    quote: "영화부터 스포츠까지, 함께 볼수록 더 즐겁게",
    quoteEmphasis: "가족과 이웃이 한 화면을 나누는 시네마",
    sectionTitle: "시네마 소개",
    cards: [
      {
        eyebrow: "PRIVATE CINEMA",
        title: "작은 영화관",
        description: "넷플릭스, 디즈니 + 등 OTT 시청",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-small-cinema-kids-3d-v1.png",
      },
      {
        eyebrow: "CHEER TOGETHER",
        title: "함께 응원",
        description: "월드컵, 야구 등 스포츠를 함께 응원",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-cheer-korea-family-3d-v1.png",
      },
      {
        eyebrow: "TEENIEPING TOGETHER",
        title: "다같이 티니핑",
        description: "우리 아이들이 사랑하는 캐릭터 친구들과 함께 보며 마음껏 웃고 즐겨요. 아이들은 신나게, 부모님은 잠시 여유롭게.",
        imageSrc: "/images/hpoint-renewal/book-cafe/book-cafe-teenieping-kids-cheering-3d-v1.png",
      },
    ],
  },
};

const BOARD_GAME_SWIPE_EYEBROWS = ["KIDS", "TEENS", "ALL GAMES"];

const BookCafeStoryDetail = ({
  feature,
  story,
  onReserve,
}: {
  feature: BookCafeFeature;
  story: BookCafeStoryContent;
  onReserve: () => void;
}) => {
  const titleId = `book-cafe-${feature.slug}-title`;
  const spacesTitleId = `book-cafe-${feature.slug}-spaces-title`;

  return (
    <main className="hpoint-renewal hp-reservation-page hp-book-cafe-detail hp-book-cafe-story">
      <article aria-labelledby={titleId}>
        <figure className="hp-book-cafe-story__hero">
          <img src={feature.image?.src} alt={feature.image?.alt} />
        </figure>

        <header className="hp-book-cafe-story__headline">
          <span>{feature.eyebrow}</span>
          <h1 id={titleId}>{feature.pageTitle ?? feature.title}</h1>
          <p>{feature.summary}</p>
        </header>

        <div className="hp-book-cafe-story__body">
          <section className="hp-book-cafe-story__copy" aria-label={story.introLabel}>
            {story.introContent ?? (
              <>
                <p>{feature.detailDescription}</p>
                <blockquote>
                  “{story.quote}”
                  <strong>{story.quoteEmphasis}</strong>
                </blockquote>
              </>
            )}
          </section>

          <section className="hp-book-cafe-story__spaces" aria-labelledby={spacesTitleId}>
            <header>
              <h2 id={spacesTitleId}>{story.sectionTitle}</h2>
            </header>
            <Swiper
              className="hp-book-cafe-story__space-swiper"
              slidesPerView="auto"
              spaceBetween={10}
            >
              {story.cards.map((card) => (
                <SwiperSlide key={card.title}>
                  <div className="hp-book-cafe-story__space-card">
                    <img src={card.imageSrc} alt="" />
                    <div>
                      <span>{card.eyebrow}</span>
                      <strong>{card.title}</strong>
                      <p>{card.description}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </section>

          {feature.slug === "family-lounge" ? (
            <p><strong className="hp-book-cafe-guardian-notice">만 13세 이하 어린이는 보호자와 함께 이용해 주세요.</strong></p>
          ) : null}

          <Button
            className="hp-button hp-button--primary hp-book-cafe-story__reserve"
            variant="contained"
            onClick={onReserve}
          >
            북카페 예약하기
          </Button>
        </div>
      </article>
    </main>
  );
};

const BookCafeBoardGamesDetail = ({
  feature,
  onReserve,
  onOpenBoardGameList,
}: {
  feature: BookCafeFeature;
  onReserve: () => void;
  onOpenBoardGameList: () => void;
}) => {
  const [isListOpen, setIsListOpen] = useState(false);
  const [isBoardGameListCoachMarkVisible, setIsBoardGameListCoachMarkVisible] = useState(
    () => window.localStorage.getItem(BOARD_GAME_LIST_COACH_MARK_SEEN_KEY) !== "true",
  );
  const [selectedAudience, setSelectedAudience] = useState<BoardGameAudience>("all");
  const filteredBoardGames = selectedAudience === "all"
    ? BOOK_CAFE_BOARD_GAMES
    : BOOK_CAFE_BOARD_GAMES.filter((game) => getBoardGameAudience(game.age) === selectedAudience);
  const selectedAudienceLabel = BOARD_GAME_AUDIENCE_FILTERS.find((filter) => filter.id === selectedAudience)?.label;

  useEffect(() => {
    if (isBoardGameListCoachMarkVisible) {
      window.localStorage.setItem(BOARD_GAME_LIST_COACH_MARK_SEEN_KEY, "true");
    }
  }, [isBoardGameListCoachMarkVisible]);

  const openBoardGameList = () => {
    setIsBoardGameListCoachMarkVisible(false);
    onOpenBoardGameList();
    setSelectedAudience("all");
    setIsListOpen(true);
  };

  return (
    <>
      {isBoardGameListCoachMarkVisible && (
        <button
          className="hp-book-cafe-board-game-list__coach-mark-backdrop"
          type="button"
          aria-label="새 기능 안내 닫기"
          onClick={() => setIsBoardGameListCoachMarkVisible(false)}
        />
      )}
      <main className="hpoint-renewal hp-reservation-page hp-book-cafe-detail hp-book-cafe-story">
        <article aria-labelledby="book-cafe-board-games-title">
          <figure className="hp-book-cafe-story__hero">
            <img src={feature.image?.src} alt={feature.image?.alt} />
          </figure>

          <header className="hp-book-cafe-story__headline">
            <span>{feature.eyebrow}</span>
            <h1 id="book-cafe-board-games-title">{feature.pageTitle ?? feature.title}</h1>
            <p>{feature.summary}</p>
            <small className="hp-book-cafe-guardian-notice"><GroupsOutlinedIcon aria-hidden="true" /> 어린이는 보호자 동반 필수</small>
          </header>

          <div className="hp-book-cafe-story__body">
            <section className="hp-book-cafe-story__copy" aria-label="보드게임 공간 소개">
              <p>{feature.detailDescription}</p>
              <blockquote>
                “{feature.detailTitle}”
                <strong>가볍게 즐기는 파티 게임부터 전략 게임까지</strong>
              </blockquote>
            </section>

            {feature.usageNotice && (
              <aside className="hp-book-cafe-story__notice" aria-label="보드게임 이용 안내">
                <strong>보드게임 이용 안내</strong>
                <p>{feature.usageNotice}</p>
              </aside>
            )}

            <section className="hp-book-cafe-story__spaces" aria-labelledby="book-cafe-board-games-spaces-title">
              <header>
                <h2 id="book-cafe-board-games-spaces-title">보드게임 소개</h2>
                <Button
                  className={`hp-book-cafe-board-game-list__trigger${
                    isBoardGameListCoachMarkVisible ? " hp-book-cafe-board-game-list__trigger--spotlight" : ""
                  }`}
                  variant="outlined"
                  onClick={openBoardGameList}
                  aria-label="보드게임 리스트"
                  aria-describedby={isBoardGameListCoachMarkVisible ? "board-game-list-coach-mark" : undefined}
                >
                  보드게임 리스트
                  {isBoardGameListCoachMarkVisible && (
                    <span id="board-game-list-coach-mark" className="hp-book-cafe-board-game-list__coach-mark" role="tooltip">
                      보드게임 종류 확인
                    </span>
                  )}
                </Button>
              </header>
              <Swiper
                className="hp-book-cafe-story__space-swiper"
                slidesPerView="auto"
                spaceBetween={10}
              >
                {feature.detailSections.map((section, sectionIndex) => {
                  const video = section.media?.video;

                  return (
                    <SwiperSlide key={section.title}>
                      <div className="hp-book-cafe-story__space-card hp-book-cafe-story__space-card--board-game">
                        {video ? (
                          <figure className="hp-book-cafe-story__space-video">
                            <iframe
                              src={video.embedSrc}
                              title={video.title}
                              loading="lazy"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              referrerPolicy="strict-origin-when-cross-origin"
                              allowFullScreen
                            />
                            <a href={video.externalSrc} target="_blank" rel="noreferrer">
                              YouTube에서 보기 <span aria-hidden="true">↗</span>
                            </a>
                          </figure>
                        ) : feature.image ? (
                          <img
                            src="/images/hpoint-renewal/book-cafe/book-cafe-board-games-collection-v2.webp"
                            alt="다양한 보드게임 상자 모음"
                            loading="lazy"
                          />
                        ) : null}
                        <div>
                          <span>{BOARD_GAME_SWIPE_EYEBROWS[sectionIndex]}</span>
                          <strong>{section.title}</strong>
                          {section.description && <p>{section.description}</p>}
                          {section.items.length > 0 && (
                            <ul>
                              {section.items.map((item) => <li key={item}>{item}</li>)}
                            </ul>
                          )}
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </section>

            <Button
              className="hp-button hp-button--primary hp-book-cafe-story__reserve"
              variant="contained"
              onClick={onReserve}
            >
              북카페 예약하기
            </Button>
          </div>
        </article>
      </main>

      <Dialog
        className="hp-book-cafe-board-game-list"
        fullScreen
        open={isListOpen}
        onClose={() => setIsListOpen(false)}
        aria-labelledby="book-cafe-board-game-list-title"
      >
        <header className="hp-book-cafe-board-game-list__header">
          <div>
            <span>BOOK CAFE</span>
            <h2 id="book-cafe-board-game-list-title">보드게임 리스트</h2>
          </div>
          <button type="button" onClick={() => setIsListOpen(false)} aria-label="닫기">
            <span aria-hidden="true">×</span>
          </button>
        </header>
        <DialogContent className="hp-book-cafe-board-game-list__content">
          <p className="hp-book-cafe-board-game-list__intro">
            함께할 사람과 이용 시간에 맞는 보드게임을 골라 즐겨보세요.
          </p>
          <div className="hp-book-cafe-board-game-list__filters" aria-label="권장 연령 필터">
            {BOARD_GAME_AUDIENCE_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                aria-pressed={selectedAudience === filter.id}
                className={selectedAudience === filter.id ? "is-active" : undefined}
                onClick={() => setSelectedAudience(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {filteredBoardGames.length > 0 ? (
            <div className="hp-book-cafe-board-game-list__grid">
              {filteredBoardGames.map((game) => (
              <article
                key={game.name}
                className="hp-book-cafe-board-game-list__card"
                aria-label={game.name}
                data-testid="book-cafe-board-game-card"
              >
                <div className="hp-book-cafe-board-game-list__media">
                  {game.image ? (
                    <img src={game.image.src} alt={game.image.alt} loading="lazy" />
                  ) : (
                    <div
                      className="hp-book-cafe-board-game-list__image-fallback"
                      aria-label={`${game.name} 이미지 준비 중`}
                    >
                      <span>이미지 준비 중</span>
                      <small>BOARD GAME</small>
                    </div>
                  )}
                </div>
                <div className="hp-book-cafe-board-game-list__card-overlay">
                  <span className="hp-book-cafe-board-game-list__age">{game.age}</span>
                  <h3>{game.name}</h3>
                  <strong className="hp-book-cafe-board-game-list__meta">
                    {game.players} · {game.playTime}
                  </strong>
                  <p>{game.description}</p>
                </div>
              </article>
              ))}
            </div>
          ) : (
            <p className="hp-book-cafe-board-game-list__empty" role="status">
              {selectedAudienceLabel} 대상 보드게임이 준비 중입니다.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const BookCafeFeatureDetail = (props: PropsType) => {
  const navigate = useNavigate();
  const { featureId } = useParams();
  const feature = getBookCafeFeature(featureId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [featureId]);

  if (!feature) {
    return (
      <main className="hpoint-renewal hp-reservation-page hp-book-cafe-detail hp-book-cafe-detail--missing">
        <span className="hp-book-cafe-guide__section-kicker">BOOK CAFE</span>
        <h1>안내를 찾을 수 없습니다</h1>
        <p>주소가 변경되었거나 제공하지 않는 북카페 안내입니다.</p>
        <Button
          className="hp-button hp-button--primary"
          variant="contained"
          onClick={() => navigate("/reservationbookcafe")}
        >
          북카페 안내로 돌아가기
        </Button>
      </main>
    );
  }

  const sendBookCafeClickLog = (eventType: string, path: string, menuName: string) => {
    try {
      void mainApi
        .put_dashboard_event({
          eventType,
          userId: props.userState.userId,
          userName: props.userState.userName,
          userDong: props.userState.userDong,
          userHo: props.userState.userHo,
          path,
          menuName,
          source: "BOOK_CAFE_GUIDE_DETAIL",
        })
        .catch(() => undefined);
    } catch {
      // Logging must never block navigation or dialog opening.
    }
  };

  const moveToReservation = () => {
    sendBookCafeClickLog("BOOK_CAFE_MENU_CLICK", "/reservationbookcafe/reserve", feature.title);
    navigate("/reservationbookcafe/reserve");
  };

  const story = BOOK_CAFE_STORY_CONTENT[feature.slug];
  if (story) {
    return <BookCafeStoryDetail feature={feature} story={story} onReserve={moveToReservation} />;
  }

  if (feature.slug === "board-games") {
    return (
      <BookCafeBoardGamesDetail
        feature={feature}
        onReserve={moveToReservation}
        onOpenBoardGameList={() => sendBookCafeClickLog(
          "BOOK_CAFE_BOARD_GAME_LIST_CLICK",
          "/reservationbookcafe/guide/board-games",
          "보드게임 리스트",
        )}
      />
    );
  }

  return (
    <main className={`hpoint-renewal hp-reservation-page hp-book-cafe-detail hp-book-cafe-detail--${feature.accent}`}>
      <header className="hp-book-cafe-detail__header">
        <button type="button" className="hp-book-cafe-detail__back" onClick={() => navigate("/reservationbookcafe")}>
          <span aria-hidden="true">←</span> 북카페 안내
        </button>
        <span className="hp-book-cafe-guide__section-kicker">{feature.eyebrow}</span>
        <h1>{feature.pageTitle ?? feature.title}</h1>
        <p>{feature.summary}</p>
      </header>

      <div
        className={`hp-book-cafe-image-slot hp-book-cafe-image-slot--detail${feature.image ? " hp-book-cafe-image-slot--filled" : ""}`}
        aria-label={feature.image ? undefined : `${feature.title} 이미지 준비 중`}
      >
        {feature.image ? (
          <img src={feature.image.src} alt={feature.image.alt} />
        ) : (
          <>
            <span>이미지 준비 중</span>
            <small>{feature.eyebrow}</small>
          </>
        )}
      </div>

      <section className="hp-book-cafe-detail__lead" aria-labelledby="book-cafe-detail-lead-title">
        <h2 id="book-cafe-detail-lead-title">{feature.detailTitle}</h2>
        <p>{feature.detailDescription}</p>
        <ul className="hp-book-cafe-detail__chips" aria-label={`${feature.title} 핵심 시설`}>
          {feature.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
        </ul>
      </section>

      <div className="hp-book-cafe-detail__sections">
        {feature.detailSections.map((section, sectionIndex) => (
          <section key={section.title} aria-labelledby={`book-cafe-detail-section-${sectionIndex}`}>
            <span aria-hidden="true">{String(sectionIndex + 1).padStart(2, "0")}</span>
            <div>
              <h2 id={`book-cafe-detail-section-${sectionIndex}`}>{section.title}</h2>
              {section.description && <p className="hp-book-cafe-detail__section-description">{section.description}</p>}
              {section.media && (
                <div className="hp-book-cafe-detail__media">
                  <figure className="hp-book-cafe-detail__media-image">
                    <img src={section.media.image.src} alt={section.media.image.alt} loading="lazy" />
                  </figure>
                  <figure className="hp-book-cafe-detail__media-video">
                    <iframe
                      src={section.media.video.embedSrc}
                      title={section.media.video.title}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                    <a href={section.media.video.externalSrc} target="_blank" rel="noreferrer">
                      YouTube에서 보기 <span aria-hidden="true">↗</span>
                    </a>
                  </figure>
                </div>
              )}
              {section.items.length > 0 && (
                <ul>
                  {section.items.map((item) => <li key={item}>{item.startsWith("만 13세 이하") ? <strong className="hp-book-cafe-guardian-notice">{item}</strong> : item}</li>)}
                </ul>
              )}
            </div>
          </section>
        ))}
      </div>

      <aside className="hp-book-cafe-detail__notice" aria-label="북카페 공통 이용 안내">
        <strong>모든 공간에 공통으로 적용돼요</strong>
        <p><strong className="hp-book-cafe-guardian-notice">만 13세 이하 어린이는 보호자와 함께 이용해 주세요.</strong></p>
      </aside>

      <div className="hp-book-cafe-detail__actions">
        <Button
          className="hp-button hp-button--primary"
          variant="contained"
          onClick={moveToReservation}
        >
          북카페 예약하기
        </Button>
        <Button variant="outlined" onClick={() => navigate("/reservationbookcafe")}>
          다른 공간 둘러보기
        </Button>
      </div>
    </main>
  );
};

export default BookCafeFeatureDetail;
